/**
 * A physical Entry the Character owns, as the engine prepares it (Foundry: item/physical/
 * document.ts with the armor, shield, container, treasure and ammo subclasses): the copy's carry
 * state, usage, runes and material over the Entry's own; the bulk data, price, level, rarity and
 * generated name they yield; and the roll options predicates test. Weapons extend this in
 * ./weapons.ts. The inventory (./inventory.ts) links containers and totals bulk.
 */
import { Bulk, STACK_DEFINITIONS, isSize, type Size } from "./bulk";
import type { Catalogue } from "./catalogue";
import { Coins, type RawCoins } from "./coins";
import { EngineItem, type EngineItemInput } from "./items";
import { METAL_MATERIALS, isMaterialGrade, isPreciousMaterial, materialLightens, materialValuation } from "./materials-data";
import { abpAttackPotency, abpDefensePotency } from "./abp";
import { ARMOR_PROPERTY_RUNES, RARITY_ORDER, REINFORCING_RUNES, RUNE_DATA, WEAPON_PROPERTY_RUNES, propertyRuneSlots, prunePropertyRunes, runeValuations } from "./runes-data";
import { sluggify } from "./slug";
import { PHYSICAL_ITEM_TYPES } from "./values";

export type CarryType = "held" | "worn" | "stowed" | "dropped";
export const CARRY_TYPES: readonly CarryType[] = ["held", "worn", "stowed", "dropped"];
export type UsageType = "held" | "worn" | "attached" | "installed" | "carried" | "implanted";

export interface UsageDetails {
  value: string;
  type: UsageType;
  /** The worn slot ("shoes"), or what it attaches to. */
  where: string | null;
  /** Hands a held usage needs. */
  hands: 0 | 1 | 2;
}

/** Foundry's `getUsageDetails`. */
export function usageDetails(usage: string | null | undefined): UsageDetails {
  const value = usage ?? "carried";
  if (value.startsWith("attached-to")) return { value, type: "attached", where: value.replace(/^attached-to-/, ""), hands: 0 };
  if (/^installed-(?:i|o)n-/.test(value)) return { value, type: "installed", where: value.replace(/^installed-(?:i|o)n-/, ""), hands: 0 };
  switch (value) {
    case "carried":
    case "":
      return { value: "carried", type: "carried", where: null, hands: 0 };
    case "held-in-one-hand":
    case "held-in-one-plus-hands":
    case "held-in-one-or-two-hands":
      return { value, type: "held", where: null, hands: 1 };
    case "held-in-two-hands":
      return { value, type: "held", where: null, hands: 2 };
    case "worn":
      return { value, type: "worn", where: null, hands: 0 };
    case "implanted":
      return { value, type: "implanted", where: null, hands: 0 };
    default:
      if (value.startsWith("worn") && value.length > 4) return { value, type: "worn", where: value.slice(4), hands: 0 };
      return { value, type: "worn", where: null, hands: 0 };
  }
}

export interface EquippedState {
  carryType: CarryType;
  handsHeld: number;
  inSlot: boolean | null;
}

/** Foundry's `isEquipped`: a carried item always, a worn item in its slot, a held item in enough hands. */
export function isEquippedFor(usage: UsageDetails, equipped: EquippedState): boolean {
  if (equipped.carryType === "dropped") return false;
  if (usage.type === "carried") return true;
  if (usage.type !== equipped.carryType) return false;
  if (usage.type === "worn" && usage.where && !equipped.inSlot) return false;
  if (usage.type === "held") return equipped.handsHeld >= usage.hands;
  return true;
}

export interface ItemRunes {
  potency: number;
  striking: number;
  resilient: number;
  reinforcing: number;
  property: string[];
}

export interface ItemMaterial {
  type: string | null;
  grade: string | null;
}

export interface BulkData {
  /** The bulk when held or stowed (armour is one step heavier off the body). */
  heldOrStowed: number;
  /** The bulk that applies now. */
  value: number;
  /** Items per unit of bulk: ten arrows, a thousand coins. */
  per: number;
  /** Containers: what they hold, and how much of it they take off the bearer. */
  capacity: number;
  ignored: number;
}

export interface EnginePhysicalInput extends EngineItemInput {
  /** The inventory item's id; a direct input gets one made up. */
  inventoryId?: string | undefined;
  quantity?: number | undefined;
  carryType?: CarryType | undefined;
  handsHeld?: number | undefined;
  inSlot?: boolean | undefined;
  invested?: boolean | null | undefined;
  containerId?: string | null | undefined;
  size?: string | undefined;
  /** Current Hit Points, when the item has been damaged. */
  hp?: number | undefined;
  /** Runes etched on this copy, over the Entry's own. */
  runes?: Partial<ItemRunes> | undefined;
  material?: ItemMaterial | null | undefined;
  /** The inventory id of the ammunition a weapon has selected. */
  ammo?: string | null | undefined;
  /**
   * The Character's Level under Automatic Bonus Progression (#12), null or absent otherwise:
   * fundamental runes are stripped (Foundry's `ABP.cleanupRunes`), property rune slots follow the
   * attack or defense potency of the Level, and a weapon's striking dice come from the Level.
   */
  abpLevel?: number | null | undefined;
}

export function isPhysicalType(type: string): boolean {
  return (PHYSICAL_ITEM_TYPES as readonly string[]).includes(type);
}

let counter = 0;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
function num(value: unknown, fallback = 0): number {
  return typeof value === "number" && Number.isFinite(value) ? value : fallback;
}
function str(value: unknown): string | null {
  return typeof value === "string" && value ? value : null;
}

/** Foundry's stack groups by ammunition base type (DEFAULT_AMMO_STACK_GROUPS and the rounds tables). */
function ammoStackGroup(baseItem: string | null): string | null {
  switch (baseItem) {
    case "arrows": return "arrows";
    case "bolts": return "bolts";
    case "sling-bullets": return "slingBullets";
    case "blowgun-darts": return "blowgunDarts";
    case "wooden-taws": return "woodenTaws";
    case "rounds": return "rounds10";
    default:
      if (baseItem?.startsWith("rounds-")) return ROUND5_FIREARMS.has(baseItem.slice(7)) ? "rounds5" : "rounds10";
      return null;
  }
}
const ROUND5_FIREARMS = new Set(["dwarven-scattergun", "explosive-dogslicer", "flingflenser"]);

export class EnginePhysical extends EngineItem {
  readonly inventoryId: string;
  quantity: number;
  carryType: CarryType;
  handsHeld: number;
  inSlot: boolean | null;
  invested: boolean | null;
  containerId: string | null;
  size: Size;
  readonly usage: UsageDetails;
  /** Set by the inventory: the container this item sits in, and what this container holds. */
  container: EnginePhysical | null = null;
  contents: EnginePhysical[] = [];
  bulkData: BulkData;
  /** The whole Bulk a precious material's price goes by (held or stowed, before any lightening). */
  priceBulk = 0;
  /** The price of `pricePer` items after runes and material. */
  price: Coins;
  pricePer: number;
  /** The Entry's own price, before runes and material. */
  readonly basePrice: Coins;
  readonly baseLevel: number;
  readonly baseRarity: string;
  itemLevel: number;
  itemRarity: string;
  /** The name Foundry generates from base type, material and runes ("+1 Striking Longsword"), when it applies. */
  generatedName: string | null = null;
  readonly ammo: string | null;
  /** The Character's Level under Automatic Bonus Progression (#12), else null. */
  readonly abpLevel: number | null;
  /** Problems with this copy's data (a rune the item cannot hold), for the sheet. */
  readonly itemProblems: string[] = [];

  constructor(input: EnginePhysicalInput) {
    super(input);
    const system = this.system;
    this.inventoryId = input.inventoryId ?? `direct:${++counter}`;
    const equipped = isRecord(system.equipped) ? system.equipped : {};
    this.carryType = input.carryType ?? ((CARRY_TYPES as readonly string[]).includes(String(equipped.carryType)) ? (equipped.carryType as CarryType) : "worn");
    this.handsHeld = this.carryType === "held" ? Math.max(0, Math.min(2, input.handsHeld ?? num(equipped.handsHeld, 1))) : 0;
    this.quantity = Math.max(0, Math.trunc(input.quantity ?? num(system.quantity, 1)));
    this.containerId = input.containerId ?? null;
    this.size = isSize(input.size) ? input.size : isSize(system.size) ? system.size : "med";
    this.ammo = input.ammo ?? null;
    this.abpLevel = typeof input.abpLevel === "number" ? input.abpLevel : null;

    // Usage: armour is worn as armour, a shield is held in one hand, treasure is merely carried.
    const usageValue = this.type === "armor" ? "wornarmor" : this.type === "shield" ? "held-in-one-hand" : this.type === "treasure" ? "carried" : str((system.usage as { value?: unknown } | undefined)?.value) ?? "carried";
    this.usage = usageDetails(usageValue);
    system.usage = { ...(isRecord(system.usage) ? system.usage : {}), value: this.usage.value, type: this.usage.type, where: this.usage.where, hands: this.usage.hands };
    const slotted = this.usage.type === "worn" && !!this.usage.where;
    this.inSlot = slotted ? (input.inSlot ?? (typeof equipped.inSlot === "boolean" ? equipped.inSlot : false)) : null;
    system.quantity = this.quantity;
    system.size = this.size;
    system.containerId = this.containerId;

    // Material and runes, the copy's over the Entry's; armour with a fundamental rune becomes an invested item.
    const material = isRecord(system.material) ? system.material : {};
    const materialType = input.material !== undefined ? (input.material?.type ?? null) : str(material.type);
    const materialGrade = input.material !== undefined ? (input.material?.grade ?? null) : str(material.grade);
    system.material = { type: isPreciousMaterial(materialType) ? materialType : null, grade: materialType && isMaterialGrade(materialGrade) ? materialGrade : null, effects: [] };
    this.prepareRunes(input.runes);
    const investable = this.traits.includes("invested");
    this.invested = investable ? (input.invested ?? (typeof equipped.invested === "boolean" ? equipped.invested : false)) : null;
    system.equipped = { carryType: this.carryType, handsHeld: this.handsHeld, ...(this.inSlot === null ? {} : { inSlot: this.inSlot }), invested: this.invested };

    // Hit points and hardness (a shield's rise with material and reinforcing rune).
    const hp = isRecord(system.hp) ? system.hp : {};
    let max = num(hp.max);
    let hardness = num(system.hardness);
    if (this.type === "shield") {
      const valuation = materialValuation(this);
      const reinforcing = REINFORCING_RUNES[this.runes.reinforcing as 1 | 2 | 3 | 4 | 5 | 6];
      const adjust = (property: "hardness" | "maxHP", base: number): number => {
        const fromMaterial = this.isSpecific ? Math.max(base, valuation?.[property] ?? base) : (valuation?.[property] ?? base);
        const sum = fromMaterial + (reinforcing?.[property].increase ?? 0);
        return reinforcing && sum > reinforcing[property].max ? Math.max(fromMaterial, reinforcing[property].max) : sum;
      };
      hardness = adjust("hardness", hardness);
      max = adjust("maxHP", max);
    }
    system.hardness = hardness;
    system.hp = { max, value: Math.max(0, Math.min(max, input.hp ?? num(hp.value, max))), brokenThreshold: Math.floor(max / 2) };

    // Price: the Entry's, then runes and material.
    const price = isRecord(system.price) ? system.price : {};
    this.basePrice = new Coins(isRecord(price.value) ? (price.value as RawCoins) : {});
    this.pricePer = Math.max(1, num(price.per, 1));
    this.baseLevel = num((system.level as { value?: unknown } | undefined)?.value);
    this.baseRarity = this.rarity ?? "common";
    this.bulkData = this.prepareBulkData();
    this.itemLevel = this.baseLevel;
    this.itemRarity = this.baseRarity;
    this.price = this.basePrice;
    this.prepareDerivedData();
  }

  /**
   * Foundry's `prepareDerivedData`: level, rarity and price from runes, material, shoddiness and
   * size. Run again by a subclass whose base preparation changes the traits (a weapon's runes add
   * the magical trait).
   */
  prepareDerivedData(): void {
    const system = this.system;
    const derived = this.computeLevelRarityPrice();
    this.itemLevel = derived.level;
    this.itemRarity = derived.rarity;
    this.price = derived.price;
    system.level = { value: this.itemLevel };
    this.traitsRecord().rarity = this.itemRarity;
    system.price = { value: this.price.toObject(), per: this.pricePer, sizeSensitive: !(this.isMagical || this.traits.includes("tech")) };
    if (this.type === "shield" && (this.isBroken || this.isDestroyed)) system.acBonus = 0;
  }

  /* --------------------------------- Accessors --------------------------------- */

  get runes(): ItemRunes {
    const r = isRecord(this.system.runes) ? this.system.runes : {};
    return { potency: num(r.potency), striking: num(r.striking), resilient: num(r.resilient), reinforcing: num(r.reinforcing), property: Array.isArray(r.property) ? r.property.filter((p): p is string => typeof p === "string" && !!p) : [] };
  }
  get material(): ItemMaterial {
    const m = isRecord(this.system.material) ? this.system.material : {};
    return { type: str(m.type), grade: str(m.grade) };
  }
  get baseType(): string | null {
    return str(this.system.baseItem);
  }
  get group(): string | null {
    return str(this.system.group);
  }
  get isMagical(): boolean {
    return this.traits.some((t) => t === "magical" || ["arcane", "divine", "occult", "primal"].includes(t));
  }
  get isSpecific(): boolean {
    return !!this.system.specific;
  }
  get isShoddy(): boolean {
    return this.otherTags.includes("shoddy");
  }
  get isEquipped(): boolean {
    return isEquippedFor(this.usage, { carryType: this.carryType, handsHeld: this.handsHeld, inSlot: this.inSlot });
  }
  get isHeld(): boolean {
    return this.carryType === "held" && this.handsHeld > 0;
  }
  get isWorn(): boolean {
    return this.carryType === "worn";
  }
  /** Inside a stowing container (Foundry: isStowed), or stowed on its own. */
  get isStowed(): boolean {
    return this.carryType === "stowed" || (!!this.container && (this.container.stowsItems || this.container.isStowed));
  }
  get isInContainer(): boolean {
    return this.container !== null;
  }
  /** True when invested, false when it needs investing and is not, null when it takes no investiture. */
  get isInvested(): boolean | null {
    if (this.invested === null) return null;
    return (this.isEquipped || !["implanted", "worn"].includes(this.usage.type)) && !this.isStowed && this.invested === true;
  }
  get stowsItems(): boolean {
    return this.type === "backpack" && this.system.stowing !== false;
  }
  get hitPoints(): { value: number; max: number; brokenThreshold: number } {
    const hp = isRecord(this.system.hp) ? this.system.hp : {};
    return { value: num(hp.value), max: num(hp.max), brokenThreshold: num(hp.brokenThreshold) };
  }
  get hardness(): number {
    return num(this.system.hardness);
  }
  get isBroken(): boolean {
    const hp = this.hitPoints;
    return hp.max > 0 && !this.isDestroyed && hp.value <= hp.brokenThreshold;
  }
  get isDestroyed(): boolean {
    const hp = this.hitPoints;
    return hp.max > 0 && hp.value === 0;
  }
  get apexAttribute(): string | null {
    const apex = isRecord(this.system.apex) ? this.system.apex : null;
    return apex && this.traits.includes("apex") ? str(apex.attribute) : null;
  }
  /** The value of the whole stack (Foundry: assetValue). */
  get assetValue(): Coins {
    return Coins.fromPrice({ value: this.price.toObject(), per: this.pricePer }, this.quantity);
  }
  /** The name shown: the generated one when it applies, else the Entry's (adjusted) name. */
  get displayName(): string {
    return this.generatedName ?? this.name;
  }

  /** Whether this item and the input describe the same kind of copy (Foundry: isStackableWith, without the identity checks). */
  stacksWith(other: EnginePhysical): boolean {
    if (this === other || this.type !== other.type || this.uuid !== other.uuid || this.type === "backpack") return false;
    if (this.type === "armor" || this.type === "shield" || this.type === "weapon") return false;
    const same = (a: unknown, b: unknown): boolean => JSON.stringify(a) === JSON.stringify(b);
    return same(this.runes, other.runes) && same(this.material, other.material) && this.size === other.size && this.isHeld === other.isHeld;
  }

  /* ------------------------------- Preparation ------------------------------- */

  private prepareRunes(override: Partial<ItemRunes> | undefined): void {
    const system = this.system;
    const source = isRecord(system.runes) ? system.runes : {};
    // Automatic Bonus Progression strips the fundamental runes (Foundry: ABP.cleanupRunes) and gives the slots itself.
    const abp = this.abpLevel;
    const clamp = (value: unknown, max: number): number => (abp !== null ? 0 : Math.max(0, Math.min(max, Math.trunc(num(value)))));
    const property = Array.isArray(override?.property) ? override.property : Array.isArray(source.property) ? source.property : isRecord(source.property) ? Object.values(source.property) : [];
    if (this.type === "weapon") {
      const runes = { potency: clamp(override?.potency ?? source.potency, 4), striking: clamp(override?.striking ?? source.striking, 4), property: prunePropertyRunes(property as string[], WEAPON_PROPERTY_RUNES) };
      const slots = abp !== null ? propertyRuneSlots({ material: this.material, runes: { potency: abpAttackPotency(abp) } }) : propertyRuneSlots({ material: this.material, runes });
      if (runes.property.length > slots) {
        this.itemProblems.push(`${runes.property.length - slots} property rune${runes.property.length - slots === 1 ? "" : "s"} beyond the ${slots} slot${slots === 1 ? "" : "s"} the weapon has`);
        runes.property.length = slots;
      }
      system.runes = runes;
    } else if (this.type === "armor") {
      const runes = { potency: clamp(override?.potency ?? source.potency, 4), resilient: clamp(override?.resilient ?? source.resilient, 4), property: prunePropertyRunes(property as string[], ARMOR_PROPERTY_RUNES) };
      const slots = abp !== null ? propertyRuneSlots({ material: this.material, runes: { potency: abpDefensePotency(abp) } }) : propertyRuneSlots({ material: this.material, runes });
      if (runes.property.length > slots) {
        this.itemProblems.push(`${runes.property.length - slots} property rune${runes.property.length - slots === 1 ? "" : "s"} beyond the ${slots} slot${slots === 1 ? "" : "s"} the armour has`);
        runes.property.length = slots;
      }
      system.runes = runes;
      // Traits from fundamental runes (Foundry: ArmorPF2e#prepareBaseData); under ABP, property runes alone make the armour invested.
      const traits = this.traitsRecord();
      const invested = runes.potency > 0 || runes.resilient > 0 || (abp !== null && runes.property.length > 0);
      if (invested && !traits.value.includes("invested")) traits.value.push("invested");
      const hasTradition = traits.value.some((t) => ["arcane", "divine", "occult", "primal"].includes(t));
      if (invested && !hasTradition && !traits.value.includes("magical")) traits.value.push("magical");
      traits.value = [...new Set(traits.value)].sort();
    } else if (this.type === "shield") {
      const runes = { reinforcing: clamp(override?.reinforcing ?? source.reinforcing, 6) };
      system.runes = runes;
      const integrated = isRecord(system.traits) && isRecord((system.traits as Record<string, unknown>).integrated) ? ((system.traits as Record<string, unknown>).integrated as Record<string, unknown>) : null;
      if (integrated) integrated.runes = { potency: 0, striking: 0, property: [], ...(isRecord(integrated.runes) ? integrated.runes : {}) };
      const traits = this.traitsRecord();
      const hasTradition = traits.value.some((t) => ["arcane", "divine", "occult", "primal"].includes(t));
      if (runes.reinforcing > 0 && !hasTradition && !traits.value.includes("magical")) traits.value.push("magical");
      traits.value = [...new Set(traits.value)].sort();
    } else {
      system.runes = { potency: 0, striking: 0, resilient: 0, reinforcing: 0, property: [] };
    }
  }

  protected traitsRecord(): { value: string[]; otherTags: string[] } & Record<string, unknown> {
    const traits = isRecord(this.system.traits) ? this.system.traits : {};
    const value = Array.isArray(traits.value) ? traits.value.filter((t): t is string => typeof t === "string") : [];
    const otherTags = Array.isArray(traits.otherTags) ? traits.otherTags.filter((t): t is string => typeof t === "string") : [];
    const record = { ...traits, value, otherTags } as { value: string[]; otherTags: string[] } & Record<string, unknown>;
    this.system.traits = record;
    return record;
  }

  /** Foundry's `prepareBulkData`, with the printed lightening of dawnsilver and duskwood. */
  private prepareBulkData(): BulkData {
    const system = this.system;
    const bulk = isRecord(system.bulk) ? system.bulk : {};
    const stackGroup = this.type === "ammo" ? ammoStackGroup(this.baseType) : this.type === "treasure" ? (system.category === "coin" ? "coins" : system.category === "gem" ? "gems" : null) : null;
    const per = stackGroup ? (STACK_DEFINITIONS[stackGroup]?.size ?? 1) : 1;
    let worn = new Bulk(num(bulk.value));
    let heldOrStowed = this.type === "armor" ? worn.increment() : typeof bulk.heldOrStowed === "number" ? new Bulk(bulk.heldOrStowed) : worn;
    // A material's price goes by the item's normal Bulk, before dawnsilver or duskwood lighten it (GM Core).
    this.priceBulk = heldOrStowed.normal;
    const material = this.material.type;
    if (material && materialLightens(material) && (material === "duskwood" || METAL_MATERIALS.includes(material))) {
      worn = worn.decrement();
      heldOrStowed = heldOrStowed.decrement();
    }
    const value = this.isEquipped ? worn.value : heldOrStowed.value;
    const data: BulkData = { heldOrStowed: heldOrStowed.value, value, per, capacity: num(bulk.capacity), ignored: num(bulk.ignored) };
    system.bulk = { ...bulk, ...data };
    return data;
  }

  /** Foundry's `computeLevelRarityPrice` and `computePrice`. */
  private computeLevelRarityPrice(): { level: number; rarity: string; price: Coins } {
    const valuation = materialValuation(this);
    const runes = runeValuations(this);
    const price = ((): Coins => {
      if (this.type === "treasure") return this.basePrice;
      const materialPrice = valuation?.price ?? 0;
      const bulk = this.type === "shield" ? 0 : Math.max(this.priceBulk, 1);
      const materialValue = this.isSpecific ? 0 : materialPrice + (bulk * materialPrice) / 10;
      const reinforcingValue = this.type === "shield" && !this.isSpecific ? (REINFORCING_RUNES[this.runes.reinforcing as 1 | 2 | 3 | 4 | 5 | 6]?.price ?? 0) : 0;
      const runeValue = this.isSpecific ? 0 : runes.reduce((sum, rune) => sum + rune.price, 0) - reinforcingValue;
      const basePrice = materialValue > 0 || runeValue > 0 ? new Coins() : this.basePrice;
      const afterMaterialAndRunes = runeValue ? new Coins({ gp: runeValue + materialValue }) : basePrice.plus({ gp: materialValue });
      const higher = afterMaterialAndRunes.copperValue > basePrice.copperValue ? afterMaterialAndRunes : basePrice;
      const afterReinforcing = higher.plus({ gp: reinforcingValue });
      const afterShoddy = this.isShoddy ? afterReinforcing.scale(0.5) : afterReinforcing;
      const sizeSensitive = !(this.isMagical || this.traits.includes("tech"));
      return sizeSensitive ? afterShoddy.adjustForSize(this.size) : afterShoddy;
    })();
    if (!(this.isMagical || valuation) || this.isSpecific) return { level: this.baseLevel, rarity: this.baseRarity, price };
    const level = Math.max(...runes.map((r) => r.level), valuation?.level ?? 0, this.baseLevel);
    const rarity = [...runes.map((r) => r.rarity), valuation?.rarity ?? "common"].reduce((highest, r) => ((RARITY_ORDER[r] ?? 0) > (RARITY_ORDER[highest] ?? 0) ? r : highest), this.baseRarity);
    return { level, rarity, price };
  }

  /**
   * Foundry's `generateItemName` for weapons, armour and shields whose stored name is their base
   * type's label: "+1 Striking Flaming Longsword", "Cold Iron Breastplate".
   */
  generateName(catalogue: Catalogue | null): string | null {
    if (!["armor", "shield", "weapon"].includes(this.type) || this.isSpecific) return null;
    const baseType = this.baseType;
    if (!baseType) return null;
    const table = this.type === "armor" ? catalogue?.table("baseArmorTypes") : this.type === "shield" ? catalogue?.table("baseShieldTypes") : { ...catalogue?.table("baseWeaponTypes"), ...catalogue?.table("baseShieldTypes") };
    const baseLabel = table?.[baseType];
    if (!baseLabel || this.entry.name !== baseLabel) return null;
    const material = this.material.type;
    const materialLabel = material ? (catalogue?.table("preciousMaterials")?.[material] ?? material) : null;
    const base = material && ["hide-armor", "steel-shield", "wooden-shield"].includes(baseType) ? { armor: "Armor", shield: "Shield", weapon: "Weapon" }[this.type as "armor" | "shield" | "weapon"] : baseLabel;
    const runes = this.runes;
    const loc = (key: string | null | undefined): string | null => (key ? (catalogue?.localize(key) ?? key) : null);
    const potency = this.type === "shield" ? null : runes.potency || null;
    const reinforcing = this.type === "shield" ? loc(REINFORCING_RUNES[runes.reinforcing as 1 | 2 | 3 | 4 | 5 | 6]?.name) : null;
    const fundamental2 = this.type === "weapon" ? loc(RUNE_DATA.weapon.striking[runes.striking as 1 | 2 | 3 | 4]?.name) : this.type === "armor" ? loc(RUNE_DATA.armor.resilient[runes.resilient as 1 | 2 | 3 | 4]?.name) : null;
    const propertyTable = this.type === "weapon" ? WEAPON_PROPERTY_RUNES : this.type === "armor" ? ARMOR_PROPERTY_RUNES : {};
    const properties = runes.property.map((p) => loc(propertyTable[p]?.name)).filter((p): p is string => !!p);
    const key = [potency ? "Potency" : null, reinforcing ? "Reinforcing" : null, fundamental2 ? "Fundamental2" : null, ["OneProperty", "TwoProperties", "ThreeProperties", "FourProperties"][properties.length - 1] ?? null, materialLabel ? "Material" : null].filter((k): k is string => !!k).join("");
    if (!key) return null;
    const template = catalogue?.localize(`PF2E.Item.Physical.GeneratedName.${key}`) ?? "";
    const values: Record<string, string> = { base, material: materialLabel ?? "", potency: String(potency ?? ""), reinforcing: reinforcing ?? "", fundamental2: fundamental2 ?? "", property1: properties[0] ?? "", property2: properties[1] ?? "", property3: properties[2] ?? "", property4: properties[3] ?? "" };
    if (template && !template.startsWith("PF2E.")) return template.replace(/\{(\w+)\}/g, (whole, name: string) => values[name] ?? whole).replace(/\s+/g, " ").trim();
    return [potency ? `+${potency}` : null, reinforcing, fundamental2, ...properties, materialLabel, base].filter((p): p is string => !!p).join(" ");
  }

  /* ------------------------------- Roll options ------------------------------- */

  override getRollOptions(prefix = this.type, includeGranter = true): string[] {
    const options = super.getRollOptions(prefix, includeGranter);
    const material = this.material;
    const entries: [string, boolean][] = [
      ["equipped", this.isEquipped],
      [`hands-held:${this.handsHeld}`, this.handsHeld > 0],
      ["uninvested", this.isInvested === false],
      [`material:${material.type}`, !!material.type],
      [`material:grade:${material.grade}`, !!material.grade],
    ];
    const runes = this.runes;
    if (this.type === "armor") {
      entries.push([`category:${this.category}`, true], [`group:${this.group ?? "none"}`, true], [`base:${this.baseType}`, !!this.baseType], [`strength:${this.system.strength}`, typeof this.system.strength === "number"], ["rune:potency", runes.potency > 0], ["rune:resilient", runes.resilient > 0], ...runes.property.map((p): [string, boolean] => [`rune:property:${sluggify(p)}`, true]));
    } else if (this.type === "shield") {
      const slug = [null, "minor", "lesser", "moderate", "greater", "major", "supreme"][runes.reinforcing] ?? null;
      entries.push([`base:${this.baseType}`, !!this.baseType], [`rune:reinforcing:${runes.reinforcing}`, runes.reinforcing > 0], [`rune:reinforcing:${slug}`, !!slug]);
    }
    options.push(...entries.filter((e) => e[1]).map((e) => `${prefix}:${e[0]}`));
    return options;
  }

  /* ----------------------------------- Bulk ----------------------------------- */

  /** This item's own bulk for its quantity, sized for the bearer (Foundry: PhysicalItemPF2e#bulk). */
  ownBulk(actorSize: Size): Bulk {
    const relevantQuantity = Math.floor(this.quantity / this.bulkData.per);
    return new Bulk(this.bulkData.value).convertToSize(this.size, actorSize).times(relevantQuantity);
  }
}
